<script setup lang="ts">
import { AppLogo, type Logo, type LogoSize } from '@clovyr/bed';

const props = withDefaults(
  defineProps<{
    logos: Logo[];
    logoSize?: LogoSize;
    logoPad?: boolean;
    selected?: boolean;
  }>(),
  {
    logos: () => [],
    logoSize: 'small',
    selected: false,
  },
);

const emit = defineEmits<{
  select: [];
}>();

const handleClick = (event: Event) => {
  // Check if click is on an interactive element that should be ignored
  const target = event.target as Element;
  if (target?.closest('button') || target?.closest('input') || target?.closest('select')) {
    return;
  }
  emit('select');
};

const logoClass = computed(() => {
  if (props.logos.length > 1) {
    return 'block-icon';
  }
  return '';
});
</script>

<template>
  <div
    class="host-selector-block"
    :class="{ 'host-selector-block--selected': selected }"
    @click="handleClick"
  >
    <div class="host-selector-block__inner background-gradient--3">
      <div class="host-selector-block__left">
        <div class="block-heading">
          <div class="block-heading__left">
            <AppLogo
              v-for="(logo, index) in logos"
              :key="index"
              :logo="logo"
              :size="logoSize"
              :pad="logoPad || logo.logoPad"
              :class="logoClass"
            />
          </div>

          <div class="block-heading__right">
            <slot name="heading-right" />
          </div>
        </div>

        <div class="block-body">
          <div class="block-body__heading">
            <slot name="body-heading" />
          </div>

          <div class="block-body__left">
            <slot name="body-left" />
          </div>

          <div class="block-body__right">
            <slot name="body-right" />
          </div>
        </div>
      </div>

      <div class="host-selector-block__right">
        <div class="radio-visual">
          <span class="radio-control" :class="{ 'radio-control--selected': selected }"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/util';

.host-selector-block {
  &__inner {
    @include util.borders-rounded;

    display: flex;
    border: 1px solid color.adjust(colors.color(background, darker), $alpha: -0.4);
    justify-content: space-between;
    padding: grid-vars.space(2.4) grid-vars.space(3.6);
    cursor: pointer;

    &:focus-within {
      border-color: colors.color(action);
    }
  }

  &__right {
    padding-top: grid-vars.space(1);
  }

  .block-heading {
    display: flex;
    align-items: center;
    justify-content: left;

    &__left {
      display: flex;
      flex-wrap: wrap;
      margin-right: grid-vars.space(1.2);
    }
  }

  .radio-control {
    display: block;
    width: grid-vars.space(2);
    height: grid-vars.space(2);
    border: 1px solid colors.color(action);
    background: transparent;
    transition: background 0.2s ease;

    @include util.borders-sphere;

    &--selected {
      background: radial-gradient(colors.color(action) 50%, rgb(255 0 0 / 0%) 51%);
    }
  }

  &--selected {
    .host-selector-block__inner {
      border-color: colors.color(action);
    }
  }
}
</style>
